/*
 * Copyright (C) 2018 Frontier Developments PLC.
 */

import {Component, Prop, Vue} from 'vue-property-decorator';
import UuidStatic from 'uuid';

/**
 * Drag/drop file form component.
 *
 * This component can be used to allow a user to drag/drop a file onto a page.
 *
 * @author Jonny Cook <jcook@frontier.co.uk>
 */
@Component({
    name: 'c-file-drag-drop',
})
export default class FileDragDrop extends Vue {
    @Prop(Boolean) public multiple!: boolean;
    @Prop([String, Array]) public accept!: string|string[];

    public dragAndDropCapable: boolean = false;
    public files: File[] = [];
    public uuid: string = UuidStatic.v4();

    /**
     * Support properties
     * @type {[format: string]: string[]}
     */
    public readonly supportConfig: {[format: string]: string[]} = {
        img: ['.jpg', '.png', 'application/octet-stream'],
        media: ['.jpg', '.png', 'application/octet-stream', '.xml', '.log', '.txt', '.zip', '.gksave'],
    };

    /**
     * Reference getter.
     *
     * @returns {string}
     */
    get reference(): string {
        return `frm_${this.uuid}`;
    }

    /**
     * Input id getter.
     *
     * @returns {string}
     */
    get inputId(): string {
        return `inp_${this.uuid}`;
    }

    /**
     * Form id getter.
     *
     * @returns {string}
     */
    get formId(): string {
        return `frm_dd_${this.uuid}`;
    }

    /**
     * Accepted types getter.
     *
     * @returns {string}
     */
    get accepted(): string {
        return this.acceptedArray.join(',');
    }

    /**
     * Accepted types getter.
     *
     * @returns {string}
     */
    get acceptedArray(): string[] {
        if (undefined === this.accept) {
            return [];
        }

        const accepted: string[] = (this.accept instanceof Array)
            ? this.accept
            : this.accept.split(',').map((item: string) => item.trim());

        const supportedItems: string[][] = Object.keys(this.supportConfig)
            .filter((type: string) => accepted.indexOf(type) > -1)
            .map((type: string) => this.supportConfig[type]);

        let supportedSet: string[] = [];
        for (const supportedItem of supportedItems) {
            supportedSet = supportedSet.concat(supportedItem);
        }

        return Array.from(new Set(supportedSet));
    }

    /**
     * File names getter.
     *
     * @returns {string}
     */
    get fileNames(): string {
        if (undefined === this.files) {
            return '';
        }

        return this.files.map((file: File) => file.name).join(', ');
    }

    /**
     * Files getter.
     *
     * @returns {boolean}
     */
    get hasFiles(): boolean {
        return this.files.length > 0;
    }

    /**
     *
     * @param {string} extension
     * @returns {boolean}
     */
    public isAcceptedExtension(extension?: string): boolean {
        if (!extension) {
            return false;
        }

        const acceptedExts: string[] = this.acceptedArray;
        if (!acceptedExts.length) {
            return true;
        }

        return acceptedExts.indexOf('.' + extension.toLowerCase()) > -1;
    }

    /**
     * Remove file from list.
     *
     * @param {number} index
     */
    public removeFile(index: number): void {
        if (this.files.length <= index) {
            return;
        }

        this.files.splice(index, 1);

        this.transmitFiles();
    }

    /**
     * Empty all files.
     */
    public empty(): void {
        this.files = [];
        this.transmitFiles();
    }

    /**
     * Determines if the drag and drop functionality is in the window.
     *
     * @returns {boolean}
     */
    public canDragAndDrop(): boolean {
        // Create and test html element for drag/drop capabilities.
        const div: HTMLElement = document.createElement('div');

        // Determine if the `draggable` or the `ondragstart` and `ondrop` events are in the element
        return ( ( 'draggable' in div )
            || ( 'ondragstart' in div && 'ondrop' in div ) )
            && 'FormData' in window
            && 'FileReader' in window;
    }

    /**
     * Mounted lifecycle event.
     */
    public mounted(): void {
        this.dragAndDropCapable = this.canDragAndDrop();
        const fileform: any = this.$refs[this.reference];

        // If drag and drop capable, then we continue to bind events to our elements.
        if ( this.dragAndDropCapable ) {
            // Listen to all of the drag events and bind an event listener to each for the fileform.
            ['drag', 'dragstart', 'dragend', 'dragover', 'dragenter', 'dragleave', 'drop'].forEach(( evt: string ) => {
                fileform.addEventListener(evt, (e: Event) => {
                    e.preventDefault();
                    e.stopPropagation();
                }, false);
            });

            // Add an event listener for drop to the form
            fileform.addEventListener('drop', (e: any) => {
                this.processFiles(e.dataTransfer.files);
            });
        }

    }

    /**
     * Process files to upload.
     *
     * @param {FileList} files
     */
    public processFiles(files: FileList): void {
        try {
            if (!files) {
                throw new Error('No files found');
            }

            if (files.length > 1 && !this.multiple) {
                throw new Error('Cannot upload multiple files');
            }

            this.files = [];
            for (const file of files) {
                if (!this.isAcceptedExtension(file.name.split('.').pop())) {
                    throw new Error('Unsupported file extension');
                }

                this.files.push(file);
            }
        } catch (e) {
            this.files = [];
        } finally {
            (document.getElementById(this.formId) as HTMLFormElement).reset();
            this.transmitFiles();
        }
    }

    /**
     * File change event.
     */
    public transmitFiles(): void {
        this.$emit('input', this.files);
        this.$emit('change', this.files);
    }


    /**
     * Trigger the file input dialog.
     */
    public triggerFileDialog(): void {
        const fileInputElement: HTMLElement = document.getElementById(this.inputId) as HTMLInputElement;
        fileInputElement.click();
    }
}

